import { Button, Card, Group, Stack, Text, TextInput } from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import { api, type CollectableModel } from "../lib/api";
import { hierarchyOf } from "../lib/hierarchy";
import { EmptyState, ErrorState, InlineError, RecordCard, SectionCard } from "./ui";

/**
 * The model of a battery being collected, and the four category levels that
 * come with it.
 *
 * "After picking the customer name, the next input should be Model. Based off
 *  the model, the Product Line, Sub Cat, Main cat, Company should be auto
 *  populated."
 *
 * So the MODEL is the input and the hierarchy is derived - read-only, and
 * shown rather than hidden, because it is the confirmation that the right
 * thing was found. Model numbers differ by a character or two, and the
 * category is what tells a human at a glance that this is the 2W battery and
 * not the 4W one.
 *
 * This replaces a four-dropdown cascade that asked the technician to file the
 * battery down the tree themselves. Four levels they no longer choose are
 * four levels they cannot get wrong.
 *
 * WHY THE WHOLE LIST IS FETCHED ONCE
 *
 * The order picker searches the server on a debounce, because it searches
 * prices and stock too and there is a screen's worth of them. This list is
 * around a hundred models with no prices at all, so it comes down in one
 * request and is filtered here. A technician doing this at a customer's gate
 * is on a phone connection: one request and instant typing beats a round trip
 * per keystroke, and the picker keeps working if the signal drops after the
 * page loads.
 *
 * It is NOT the order picker with the prices removed. That one refuses an
 * unpriced product - correctly, since you cannot sell what has no price - and
 * here an unpriced model is collectable like any other. Nothing on this
 * screen is being sold.
 */
export function ModelPicker({
  value,
  onChange,
  disabled = false,
  idPrefix = "model",
}: {
  /** The chosen product id, or "" for none. */
  value: string;
  onChange: (productId: string) => void;
  disabled?: boolean;
  idPrefix?: string;
}) {
  const [models, setModels] = useState<CollectableModel[]>([]);
  const [levels, setLevels] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [query, setQuery] = useState("");

  useEffect(() => {
    void (async () => {
      try {
        const loaded = await api.serviceModels();
        setModels(loaded.models);
        setLevels(loaded.levels);
      } catch (caught) {
        setError(
          caught instanceof Error
            ? caught.message
            : "Could not load the list of models.",
        );
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const chosen = useMemo(
    () => models.find((model) => model.id === value),
    [models, value],
  );

  /**
   * Matches the model number OR the name, and that is not a nicety: a battery
   * label that has been under a bonnet for four years is often half unreadable,
   * and "AMARON" or "150" out of the part that is still legible is what a
   * technician has to go on. Searching the model number alone would strand
   * them, since there is no longer any way to collect a battery without
   * naming its model.
   */
  const matches = useMemo(() => {
    const needle = query.trim().toUpperCase();
    if (needle.length < 1) return [];
    return models.filter(
      (model) =>
        model.sku.toUpperCase().includes(needle) ||
        model.name.toUpperCase().includes(needle),
    );
  }, [models, query]);

  if (error && models.length === 0) {
    return <ErrorState title="Could not load the model list" message={error} />;
  }

  // The chosen model, with the levels filled in. Replaces the search box
  // rather than sitting under it: the search has done its job.
  if (chosen) {
    return (
      <SectionCard
        title="Model"
        action={
          !disabled && (
            <Button
              variant="default"
              size="sm"
              onClick={() => {
                onChange("");
                setQuery("");
              }}
            >
              Change
            </Button>
          )
        }
      >
        <Stack gap="sm">
          <Stack gap={2}>
            <Text fz="lg" fw={600} c="gray.9" className="num">
              {chosen.sku}
            </Text>
            <Text fz="md" c="gray.7">
              {chosen.name}
            </Text>
          </Stack>
          {/* A <dl>, because these are four label/value PAIRS and the browser
              drive reads them back as an ordered list to prove the levels sit
              at the right depth. "Category" is a substring of both "Main
              category" and "Sub category", so a looser check would pass with
              a level missing. */}
          <Card withBorder={false} bg="gray.0" radius="md" p="sm">
            <Stack gap={6} component="dl" m={0}>
              {hierarchyOf(chosen).map((node) => (
                <Group key={node.level} justify="space-between" gap="sm" wrap="nowrap">
                  <Text component="dt" fz="sm" c="gray.6">
                    {levels[node.level - 1] ?? `Level ${node.level}`}
                  </Text>
                  <Text component="dd" fz="sm" fw={500} c="gray.9" m={0} ta="right">
                    {node.name}
                  </Text>
                </Group>
              ))}
            </Stack>
          </Card>
        </Stack>
      </SectionCard>
    );
  }

  return (
    <Stack gap="sm">
      <TextInput
        id={`${idPrefix}-search`}
        label="Model"
        withAsterisk
        type="search"
        inputMode="search"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        leftSection={<IconSearch size={16} stroke={1.8} />}
        disabled={disabled || loading}
        value={query}
        onChange={(event) => setQuery(event.currentTarget.value)}
        placeholder={
          loading ? "Loading the model list…" : "Type the model number, e.g. AAM-FL-00040B20L"
        }
        description="Read it off the battery. Part of the model number or the name is enough. The company, main category, category and sub category fill in from whatever you pick."
      />
      {error && <InlineError>{error}</InlineError>}

      {query.trim().length > 0 && matches.length === 0 && !loading && (
        <EmptyState
          title={`Nothing matches “${query.trim()}”`}
          description="Try less of it — part of the name, like AMARON, will do. If the model really is not in the catalogue, the Store Manager has to add it before it can be collected."
        />
      )}

      {matches.slice(0, 25).map((model) => (
        <RecordCard
          key={model.id}
          primary={model.sku}
          secondary={model.name}
          meta={hierarchyOf(model)
            .map((node) => node.name)
            .join(" › ")}
          onClick={() => onChange(model.id)}
        />
      ))}
      {matches.length > 25 && (
        <Text fz="sm" c="gray.6">
          {matches.length - 25} more match. Type more of the model number to
          narrow it.
        </Text>
      )}
    </Stack>
  );
}
